import { swSettings } from "app/common/services/swSettings";
import { DefaultCellHeader } from "app/components/React/Table/headerCells";
import SWReactTableWrapper from "app/components/React/Table/SWReactTableWrapper";
import queryString from "query-string";
import type { FC } from "react";
import { useEffect, useMemo, useState } from "react";
import { apiHelper } from "app/common/services/apiHelper";
import DurationService from "app/services/DurationService";
import { DefaultFetchService } from "app/services/fetchService";
import { changeFilter } from "app/filters/ngFilters";
import type {
    IAdvertisersTableProps,
    IFilterItem,
} from "app/pages/website-analysis/analyze-publishers/advertisers/common/types";
import {
    getCommonColumns,
    getCompareColumns,
} from "app/pages/website-analysis/analyze-publishers/advertisers/common/ColumnsConfig";
import { AdvertisersTableTopCompare } from "app/pages/website-analysis/analyze-publishers/advertisers/compare/AdvertisersTableTopCompare";
import CountryService from "app/services/CountryService";
import { useCurrentStateAndParams } from "@uirouter/react";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { chosenSites } from "app/common/services/chosenSites";
import { QualificationSideBarHOC } from "app/components/QualificationSideBar/QualificationSideBarHOC";

const fetchService = DefaultFetchService.getInstance();

export interface IDistributionItemTable {
    [key: string]: IFilterItem[];
}

const AdvertisersTableCompare: FC<IAdvertisersTableProps> = (props) => {
    const { i18n, getLink } = props;
    const { params, state } = useCurrentStateAndParams();
    const handleOpenQualificationSideBar =
        !!state.qualificationSideBarComponent && props.handleOpenQualificationSideBar;
    const { webSource, orderBy, duration, network, country, isWWW, key, industry, search } = params;
    const { from, to, isWindow } = DurationService.getDurationData(duration).forAPI;
    const durationObject = useMemo(() => DurationService.getDurationData(duration), [duration]);
    const fromDate = durationObject.raw.from;
    const toDate = durationObject.raw.to;
    const chosenItems = chosenSites.sitelistForLegend();
    const title = i18n("analysis.monetization.advertisers.title");
    const tooltip = i18n("analysis.monetization.advertisers.title.tooltip");
    const subtitleFilters = useMemo(
        () => [
            {
                filter: "date",
                value: {
                    from: fromDate.valueOf(),
                    to: toDate.valueOf(),
                    useRangeDisplay: duration !== "28d" && fromDate.format("YYYY-MM") !== toDate.format("YYYY-MM"),
                },
            },
            {
                filter: "country",
                countryCode: country,
                value: CountryService.getCountryById(country)?.text,
            },
            {
                filter: "webSource",
                value: webSource,
            },
        ],
        [fromDate, toDate, country, webSource, duration],
    );
    const [totalSectionData, setTotalSectionData] = useState<{
        totalShare: number;
        totalUnGroupedCount: number;
        totalVisits: number;
    }>();
    const [filtersData, setFiltersData] = useState<{
        Networks: IFilterItem[];
        Categories: IFilterItem[];
    }>();
    const [categoriesForDistributionTable, setCategoriesForDistributionTable] = useState<IDistributionItemTable>();
    const [networksForDistributionTable, setNetworksForDistributionTable] = useState<IDistributionItemTable>();
    const [totalVisitsGlobalList, setTotalVisitsGlobalList] = useState<{}>();
    const [topCategories, setTopCategories] = useState<IFilterItem[]>();
    const [topNetworks, setTopNetworks] = useState<string[]>();
    const [pageNumber, setPageNumber] = useState<number>(1);
    const changePageCallback = (page) => setPageNumber(page);

    let field = "TotalShare";
    let sortDirection = "desc";
    if (orderBy) {
        [field, sortDirection] = orderBy.split(" ");
    }
    const columns = [...getCommonColumns(getLink, handleOpenQualificationSideBar), ...getCompareColumns()].map(
        (col: any) => {
            const isSorted = col.field === field;
            return {
                ...col,
                visible: col.visible !== false,
                headerComponent: col.headerComponent || DefaultCellHeader,
                isSorted,
                sortDirection,
                isResizable: col.isResizable !== false,
            };
        },
    );

    useEffect(() => {
        // setTimeout in order to prevent the "$digest already in progress" error
        setTimeout(() => {
            if (orderBy && !columns.find((col) => col.field === field)) {
                onSort({ field: "TotalShare", sortDirection: "desc" });
            }
        }, 0);
    }, []);

    // check if there is a selected filter and if it's supported in the new page state
    useEffect(() => {
        if (filtersData) {
            const params = {};
            if (network && !filtersData.Networks.find((n) => n.Name === network)) {
                params["network"] = null;
            }

            if (industry && !filtersData.Categories.find((c) => c.Name.includes(industry.replace("~", "/")))) {
                params["industry"] = null;
            }

            if (Object.keys(params).length) {
                proNavigator.applyUpdateParams(params);
            }
        }
    }, [filtersData]);

    useEffect(() => {
        async function fetchData() {
            setFiltersData(null);
            const response = await fetchService.get<{
                Networks: IFilterItem[];
                Categories: IFilterItem[];
            }>("/api/websiteanalysis/GetTrafficDisplayPaidOutgoing", {
                country,
                from,
                isWWW: isWWW !== "*",
                isWindow,
                key,
                orderBy: "Share desc",
                to,
                webSource,
            });
            setFiltersData(response);
        }

        fetchData();
    }, []);

    useEffect(() => {
        async function fetchData() {
            setTopNetworks(null);
            const response = await fetchService.get<{
                Records: { Name: string }[];
            }>("/api/websiteanalysis/GetTrafficDisplayPaidOutgoingAdsTable", {
                country,
                from,
                isWWW: isWWW !== "*",
                isWindow,
                key,
                orderBy: "TotalShare desc",
                to,
                webSource,
            });
            setTopNetworks(response.Records.map((item) => item.Name));
        }

        fetchData();
    }, []);

    const onSort = ({ field, sortDirection }) => {
        proNavigator.applyUpdateParams({
            orderBy: `${field} ${sortDirection}`,
        });
    };

    const dataParamsAdapter = (params) => {
        const transformedParams = { ...params };

        if (transformedParams.sort) {
            transformedParams.orderBy = `${params.sort} ${params.asc ? "asc" : "desc"}`;
            delete transformedParams.sort;
            delete transformedParams.asc;
        }

        const filters = [];
        if (transformedParams.network) {
            filters.push(`network;==;"${decodeURIComponent(transformedParams.network)}"`);
            delete transformedParams.network;
        }
        if (transformedParams.industry) {
            filters.push(`category;category;"${decodeURIComponent(transformedParams.industry)}"`);
            delete transformedParams.industry;
        }
        if (transformedParams.search) {
            filters.push(`domain;contains;"${transformedParams.search}"`);
            delete transformedParams.search;
        }
        if (filters.length > 0) {
            transformedParams.filter = filters.join(",");
        }

        return transformedParams;
    };

    const downloadExcelPermitted = swSettings.current.resources.IsExcelAllowed;

    const getExcelUrl = () => {
        const params = dataParamsAdapter(getInitialFilters());
        const queryStringParams = queryString.stringify(params);
        return `/export/analysis/GetTrafficDisplayPaidOutgoingWebsitesTsv?${queryStringParams}`;
    };

    const getInitialFilters = () => {
        const copiedParams = { ...params };
        // orderBy
        copiedParams.sort = field;
        copiedParams.asc = sortDirection === "asc" || false;

        if (!copiedParams.page) {
            copiedParams.page = pageNumber;
        }

        return apiHelper.transformParamsForAPI(copiedParams);
    };
    const transformData = (data) => {
        const stateName = "digitalsuite_website_websiteperformance";
        return {
            ...data,
            Records: data.Records.map((record: any) => {
                return {
                    ...record,
                    url: proNavigator.href(stateName, {
                        ...params,
                        key: record.Domain,
                    }),
                    Category: record.Category.replace(/_/g, " ").replace("~", " > "),
                    CategoryApi: record.Category,
                    Children: Array.isArray(record.Children)
                        ? record.Children.map((child) => {
                              return {
                                  ...child,
                                  url: proNavigator.href(stateName, {
                                      ...params,
                                      key: child.Domain,
                                  }),
                                  Category: record.Category.replace(/_/g, " ").replace("~", " > "),
                                  CategoryApi: record.Category,
                              };
                          })
                        : null,
                };
            }),
        };
    };

    const onGetData = (data) => {
        setTotalSectionData({
            totalShare: data.TotalShare > 0.99 ? 1 : data.TotalShare,
            totalUnGroupedCount: data.TotalUnGroupedCount,
            totalVisits: data.TotalVisits,
        });

        setTopCategories(data.TopCategories.filter((category) => category.Name !== "Unknown"));
        setCategoriesForDistributionTable(data.Categories);
        setNetworksForDistributionTable(data.Networks);
        setTotalVisitsGlobalList(transformShareBarData(data.TotalVisitsGlobalList));
    };

    const transformShareBarData = (data) => {
        const res = [];
        Object.entries(data).forEach(([domain, percentage]) => {
            if (percentage !== "NaN") {
                res.push({
                    color: chosenSites.getSiteColor(domain),
                    name: domain,
                    percentage,
                    valueText: changeFilter()(percentage),
                    width: (percentage as number) * 100,
                });
            }
        });
        return res;
    };

    const getDomains = () => {
        return key.split(",").map((site: string) => {
            return {
                color: chosenSites.getSiteColor(site),
                name: site,
            };
        });
    };
    return (
        <div data-automation="advertisers-compare">
            <SWReactTableWrapper
                tableOptions={{
                    metric: "TrafficSourcesAdvertisers",
                    defaultSorted: "TotalShare",
                    noDataTitle: i18n("analysis.publishers.advertisers.empty.state.title"),
                }}
                serverApi={`/api/websiteanalysis/GetTrafficDisplayPaidOutgoingWebsitesTable`}
                tableColumns={columns}
                initialFilters={getInitialFilters()}
                recordsField="Records"
                totalRecordsField="TotalCount"
                onSort={onSort}
                pageIndent={1}
                transformData={transformData}
                getDataCallback={onGetData}
                dataParamsAdapter={dataParamsAdapter}
                changePageCallback={changePageCallback}
            >
                {(topComponentProps) => (
                    <AdvertisersTableTopCompare
                        downloadExcelPermitted={downloadExcelPermitted}
                        excelLink={getExcelUrl()}
                        chosenItems={chosenItems}
                        searchValue={search}
                        i18n={i18n}
                        totalSectionData={totalSectionData}
                        filtersData={filtersData}
                        network={network}
                        category={industry}
                        networksDistribution={networksForDistributionTable}
                        categoriesDistribution={categoriesForDistributionTable}
                        competitiveTrafficShare={totalVisitsGlobalList}
                        topCategories={topCategories}
                        domains={getDomains()}
                        getLink={getLink}
                        topNetworks={topNetworks}
                        title={title}
                        tooltip={tooltip}
                        subtitleFilters={subtitleFilters}
                        {...topComponentProps}
                    />
                )}
            </SWReactTableWrapper>
        </div>
    );
};

export default QualificationSideBarHOC(AdvertisersTableCompare);
